import { createContext, type ReactElement, type ReactNode, useContext, useMemo } from "react"
import { useEntitlements } from "./api/useEntitlements"
import { alreadyServedRows } from "./sections"
import type { AccessWindow } from "./types"
import { type AccessWindowIndex, buildAccessWindowIndex, EMPTY_WINDOW_INDEX, type WindowLookup } from "./windows"

/**
 * Carries the seat window from the commerce rows to the `libraryV2`-backed
 * sections, which have no seat of their own.
 *
 * Context rather than props because the two halves of the page are fetched by
 * different hooks and composed side by side: Studies / Digital Events / Ebooks
 * come from `libraryV2`, the window comes from `entitlements`, and nothing in
 * between owns both. `useEntitlements` here shares TanStack Query's cache with
 * `EntitlementSections`, so this costs no extra request — including the pages
 * that section's "Load more" adds.
 *
 * Outside the provider the index is empty and cards render exactly as they did
 * before, which is also what happens with the POC facade off.
 */
const AccessWindowContext = createContext<AccessWindowIndex>(EMPTY_WINDOW_INDEX)

export const AccessWindowProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { items } = useEntitlements()

  const index = useMemo(() => buildAccessWindowIndex(alreadyServedRows(items)), [items])

  if (import.meta.env.DEV && index.ambiguousKeys.length > 0) {
    console.info(
      `[digital-library] ${index.ambiguousKeys.length} title(s) matched more than one seat with different windows, so those cards show no window: ${index.ambiguousKeys.join(", ")}`,
    )
  }

  return <AccessWindowContext.Provider value={index}>{children}</AccessWindowContext.Provider>
}

/** The seat window for a library card, or undefined when no seat matches it unambiguously. */
export const useAccessWindow = (title: string): AccessWindow | undefined => {
  const index = useContext(AccessWindowContext)

  return index.lookup(title)
}

/**
 * The same question as `useAccessWindow`, with the reason the answer is what it
 * is. Only the provenance overlay wants this — a card renders from the window or
 * it renders nothing, and must not vary on *why* there is nothing.
 */
export const useWindowLookup = (title: string): WindowLookup => {
  const index = useContext(AccessWindowContext)

  return index.resolve(title)
}

export const useAccessWindowIndex = (): AccessWindowIndex => useContext(AccessWindowContext)
